import {
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  GraduationCap,
  Headphones,
  LibraryBig,
  Pause,
  Play,
  Radio,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  Users,
} from "lucide-react";
import { useRef, useState } from "react";
import { useQaidaContent } from "../lib/content";
import { streakFrom, weekPractice } from "../lib/progress";
import { audioUrl, globalAyahNumber, loadChapterIndex } from "../lib/quran";
import { withBase } from "../lib/base";
import { LIBRARY_TOTALS } from "../data/library";
import type { AppSection, LearnerProgress } from "../types";

// The first screen.
//
// Everything here is glass over a deep navy ground — the parent brand's colour,
// not the app's green — because this page is the handshake between Al Noor
// Tuition and the Quran app, and it should look like the same organisation.
// Green survives as the accent for anything to do with recitation.
//
// Text sits on solid, high-contrast panels rather than directly on the gradient:
// a heading that lands half on a light blob and half on a dark one is the single
// most common way "premium glass" turns into something unreadable.

const PATHS = [
  {
    id: "qaida" as const,
    tag: "Start with the foundation",
    tagUr: "بنیاد سے آغاز",
    title: "Noorani Qaida",
    titleUr: "نورانی قاعدہ",
    body: "Letters, sounds, joining, madd and tajweed across carefully guided stages.",
    bodyUr: "حروف، آوازیں، جوڑ، مد اور تجوید — منظم مراحل میں۔",
    glyph: "اَ",
    icon: GraduationCap,
    tone: "emerald",
  },
  {
    id: "quran" as const,
    tag: "Read, listen & reflect",
    tagUr: "پڑھیں، سنیں، غور کریں",
    title: "The Noble Quran",
    titleUr: "مکمل قرآن",
    body: "All 114 surahs with Urdu and English meaning, recitation, and bookmarks.",
    bodyUr: "تمام ۱۱۴ سورتیں، اردو و انگریزی ترجمہ، تلاوت اور بک مارک۔",
    glyph: "اقْرَأْ",
    icon: BookOpen,
    tone: "gold",
  },
  {
    id: "classroom" as const,
    tag: "Learn together, live",
    tagUr: "استاد کے ساتھ",
    title: "Live classroom",
    titleUr: "براہِ راست کلاس",
    body: "One shared page, a marking board, video, and your teacher leading it.",
    bodyUr: "ایک مشترکہ صفحہ، بورڈ، ویڈیو اور استاد کی رہنمائی۔",
    glyph: "ﷺ",
    icon: Radio,
    tone: "sky",
  },
];

export function Home({ progress, navigate, name, language }: { progress: LearnerProgress; navigate: (section: AppSection) => void; name: string; language: "en" | "ur" }) {
  const { lessons } = useQaidaContent();
  const qaidaTotal = lessons.length || 1;
  const qaidaPercent = Math.round((progress.completedQaida.length / qaidaTotal) * 100);
  const weeklyDone = Math.min(progress.weeklyGoal, weekPractice(progress.practiceLog));
  const streak = streakFrom(progress.activeDays);
  // "Today's recitation" used to show a play button that did nothing.
  const [playing, setPlaying] = useState(false);
  const player = useRef<HTMLAudioElement | null>(null);
  const playRecitation = async () => {
    if (playing) { player.current?.pause(); setPlaying(false); return; }
    try {
      const chapters = await loadChapterIndex();
      const audio = new Audio(audioUrl(globalAyahNumber(chapters, 67, 1)));
      player.current = audio;
      audio.onended = () => setPlaying(false);
      await audio.play();
      setPlaying(true);
    } catch { setPlaying(false); }
  };
  const weeklyPercent = Math.round((weeklyDone / Math.max(progress.weeklyGoal, 1)) * 100);
  const urdu = language === "ur";

  return <div className={`page-stack home-page ${urdu ? "urdu-interface" : ""}`}>
    <section className="hero-v2">
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-grid-lines" aria-hidden="true" />

      <div className="hero-v2-copy">
        <span className="hero-badge"><img src={withBase("/brand/al-noor-mark.png")} alt="" width={22} height={22} /> {urdu ? "الن نور ٹیوشن" : "Al Noor Tuition"}</span>
        <h1>{urdu
          ? <>قرآن سیکھیں،<span className="hero-accent">دل سے جوڑیں۔</span></>
          : <>Learn the Quran.<span className="hero-accent">Keep it close.</span></>}</h1>
        <p className="hero-lede">{urdu
          ? `السلام علیکم ${name}۔ حرف کی پہلی آواز سے خوبصورت تلاوت تک — استاد کے ساتھ سیکھیں، پھر اپنی رفتار سے دہرائیں۔`
          : `Assalamu alaikum, ${name}. From the first sound of a letter to fluent recitation — learn live with your teacher, then revise at your own pace.`}</p>

        <div className="hero-v2-actions">
          <button className="button primary" onClick={() => navigate(progress.lastRead ? "quran" : "qaida")}>
            {urdu ? "اپنا سبق جاری رکھیں" : "Continue your lesson"} <ArrowRight size={17} />
          </button>
          <button className="button glass" onClick={() => navigate("classroom")}>
            <span className="live-dot" /> {urdu ? "براہِ راست کلاس" : "Join a live class"}
          </button>
        </div>

        <ul className="hero-assurances">
          <li><Check size={14} /> {urdu ? "بچوں کے لیے موزوں" : "Child-friendly"}</li>
          <li><ShieldCheck size={14} /> {urdu ? "استاد کی رہنمائی" : "Teacher-guided"}</li>
          <li><Headphones size={14} /> {urdu ? "ہر آلے پر" : "On any device"}</li>
        </ul>
      </div>

      <aside className="hero-v2-panel" aria-label={urdu ? "آج کا سبق" : "Today's lesson"}>
        <div className="glass-card lesson-preview">
          <header><span><BookOpen size={14} /> {urdu ? "آج کی تلاوت" : "Today's recitation"}</span><button className="ghost-play" aria-label={playing ? (urdu ? "روکیں" : "Pause") : (urdu ? "سنیں" : "Play")} aria-pressed={playing} onClick={() => void playRecitation()}>{playing ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}</button></header>
          <div className="surah-row"><span className="surah-no">67</span><div><b>Al-Mulk</b><small>{urdu ? "بادشاہی" : "The Sovereignty"}</small></div><i dir="rtl">الملك</i></div>
          <p className="preview-ayah" dir="rtl">تَبَارَكَ الَّذِي بِيَدِهِ الْمُلْكُ</p>
          <div className="wave" aria-hidden="true">{[14, 24, 11, 32, 21, 40, 16, 29, 19, 36, 13, 26, 17, 33, 20, 27].map((height, index) => <i key={index} style={{ height }} />)}</div>
        </div>

        <div className="glass-card progress-preview">
          <div className="ring" style={{ ["--value" as string]: `${qaidaPercent}` }} aria-hidden="true"><b>{qaidaPercent}%</b></div>
          <div>
            <b>{urdu ? "قاعدہ کی پیش رفت" : "Qaida progress"}</b>
            <small>{progress.completedQaida.length} {urdu ? "از" : "of"} {lessons.length} {urdu ? "مراحل" : "stages"}</small>
          </div>
        </div>

        <div className="glass-chip streak-chip"><Star size={14} fill="currentColor" /><div><b>{streak ? `${streak} ${urdu ? "دن مسلسل" : "day streak"}` : (urdu ? "آج سلسلہ شروع کریں" : "Start a streak today")}</b><small>{streak ? (urdu ? "ماشاءاللہ، جاری رکھیں" : "Ma sha Allah, keep going") : (urdu ? "ایک آیت یا ایک سبق کافی ہے" : "One ayah or one lesson is enough")}</small></div></div>
      </aside>
    </section>

    <section className="stat-strip" aria-label={urdu ? "پیش رفت" : "Learning progress"}>
      <article><span className="stat-icon emerald"><BookOpen size={17} /></span><div><strong>{progress.completedQaida.length}<small>/{lessons.length}</small></strong><span>{urdu ? "قاعدہ مراحل" : "Qaida stages"}</span></div><i className="bar"><span style={{ width: `${qaidaPercent}%` }} /></i></article>
      <article><span className="stat-icon gold"><CheckCircle2 size={17} /></span><div><strong>{progress.completedAyahs.length}</strong><span>{urdu ? "آیات کی مشق" : "Ayahs practised"}</span></div></article>
      <article><span className="stat-icon violet"><Sparkles size={17} /></span><div><strong>{progress.points}</strong><span>{urdu ? "پوائنٹس" : "Learning points"}</span></div></article>
      <article><span className="stat-icon sky"><Target size={17} /></span><div><strong>{weeklyDone}<small>/{progress.weeklyGoal}</small></strong><span>{urdu ? "ہفتہ وار ہدف" : "Weekly goal"}</span></div><i className="bar"><span style={{ width: `${weeklyPercent}%` }} /></i></article>
    </section>

    <section className="section-heading spacious">
      <div>
        <span className="kicker">{urdu ? "آپ کا راستہ" : "A clear learning path"}</span>
        <h2>{urdu ? "آج آپ کہاں سے شروع کریں گے؟" : "Where would you like to begin?"}</h2>
      </div>
      <button className="text-link" onClick={() => navigate("journey")}>{urdu ? "میرا سفر" : "View my journey"} <ArrowRight size={15} /></button>
    </section>

    <section className="path-grid-v2">
      {PATHS.map((path) => {
        const Icon = path.icon;
        return <button key={path.id} className={`path-card-v2 ${path.tone}`} onClick={() => navigate(path.id)}>
          <span className="path-glyph" dir="rtl" aria-hidden="true">{path.glyph}</span>
          <span className="path-icon"><Icon size={18} /></span>
          <span className="path-tag">{urdu ? path.tagUr : path.tag}</span>
          <h3>{urdu ? path.titleUr : path.title}</h3>
          <p>{urdu ? path.bodyUr : path.body}</p>
          <span className="path-go">
            {path.id === "qaida" ? <i className="bar"><span style={{ width: `${qaidaPercent}%` }} /></i>
              : path.id === "quran" ? <small>{progress.lastRead ? `${urdu ? "جاری رکھیں" : "Continue"} ${progress.lastRead.surah}:${progress.lastRead.ayah}` : (urdu ? "الفاتحہ سے شروع" : "Begin with Al-Fatihah")}</small>
                : <small>{urdu ? "استاد کے کوڈ سے شامل ہوں" : "Join with your teacher's code"}</small>}
            <ArrowRight size={16} />
          </span>
        </button>;
      })}
    </section>

    <section className="library-banner">
      <div>
        <span className="kicker"><LibraryBig size={14} /> {urdu ? "تدریسی کتب خانہ" : "The teaching library"}</span>
        <h2>{urdu ? "ہر بچے کے لیے، ہر مرحلے پر" : "Made for every child, at every stage."}</h2>
        <p>{urdu
          ? `قاعدہ اور تجوید سے لے کر دعاؤں، سیرت، احادیث اور اسلامی آداب تک — ${LIBRARY_TOTALS.resources} وسائل ${LIBRARY_TOTALS.categories} شعبوں میں۔`
          : `From Qaida and tajweed to duas, seerah, hadith, and Islamic manners — ${LIBRARY_TOTALS.resources} teaching resources across ${LIBRARY_TOTALS.categories} areas.`}</p>
        <button className="button primary" onClick={() => navigate("resources")}>{urdu ? "کتب خانہ کھولیں" : "Open the library"} <ArrowRight size={16} /></button>
      </div>
      <div className="library-banner-art" aria-hidden="true">
        <span className="art-tile emerald">قرآن</span>
        <span className="art-tile gold">تجويد</span>
        <span className="art-tile violet">دُعا</span>
        <span className="art-tile sky">سيرة</span>
      </div>
    </section>

    <section className="class-banner">
      <div className="class-banner-copy">
        <span className="kicker"><Users size={14} /> {urdu ? "انسانی رہنمائی" : "Human guidance, built in"}</span>
        <h2>{urdu ? "استاد پڑھائے۔ آپ سیکھتے رہیں۔" : "Your teacher leads. You keep learning."}</h2>
        <p>{urdu
          ? "کلاس میں ایک ہی صفحہ دیکھیں، تلفظ پر توجہ دیں، بورڈ پر مل کر نشان لگائیں — اور بعد میں اپنی پیش رفت کے ساتھ دہرائیں۔"
          : "Follow the same page in class, focus on pronunciation, mark up the board together — then come back later and revise with your progress intact."}</p>
        <button className="button glass-dark" onClick={() => navigate("classroom")}>{urdu ? "کلاس روم دیکھیں" : "See the live classroom"} <ArrowRight size={16} /></button>
      </div>
      <div className="class-banner-board" aria-hidden="true">
        <div className="board-chrome"><span><i /><i /><i /></span><b>Al Noor live room</b><span className="live-label"><i className="live-dot" /> LIVE</span></div>
        <div className="board-face">
          <span className="focus-label">{urdu ? "توجہ" : "Focus sound"}</span>
          <strong dir="rtl">ضَ</strong>
          <svg viewBox="0 0 360 120" preserveAspectRatio="none"><path d="M20 92 C 84 36, 140 112, 212 52 S 316 76, 342 20" /><circle cx="212" cy="52" r="4.5" /></svg>
        </div>
      </div>
    </section>
  </div>;
}
